/**
 * SCR-204: Redaction Upload Modal
 * BRD-014: Client-Side Document Redaction | US-204: Upload Only Redacted Version
 *
 * Modal for uploading redacted documents with metadata.
 * This component is used after client-side redaction has been applied.
 *
 * Usage:
 *   <RedactionUploadModal
 *     open={isOpen}
 *     onOpenChange={setIsOpen}
 *     redactedFile={redactedPdfBlob}
 *     originalFilename="polis-2024.pdf"
 *     redactionMetadata={{
 *       redactions: [...],
 *       templateId: 'template-1',
 *       originalHash: 'sha256...',
 *     }}
 *     onUploadComplete={(documentId) => navigate(`/documents/${documentId}`)}
 *   />
 */

import { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  ProgressBar,
  Icon,
  Select,
  FormField,
  type SelectOption,
} from '@myvault/shared';
import {
  useUploadWithRedaction,
  type RedactionMetadata,
} from '../hooks/useUploadWithRedaction';

// Category options
const CATEGORY_OPTIONS: SelectOption[] = [
  { value: '', label: 'Selecteer categorie (optioneel)' },
  { value: 'insurance', label: 'Verzekering' },
  { value: 'mortgage', label: 'Hypotheek' },
  { value: 'legal', label: 'Juridisch' },
  { value: 'medical', label: 'Medisch' },
  { value: 'financial', label: 'Financieel' },
  { value: 'other', label: 'Overig' },
];

interface RedactionUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  redactedFile: Blob;
  originalFilename: string;
  redactionMetadata: RedactionMetadata;
  onUploadComplete?: (documentId: string) => void;
  onCancel?: () => void;
}

export function RedactionUploadModal({
  open,
  onOpenChange,
  redactedFile,
  originalFilename,
  redactionMetadata,
  onUploadComplete,
  onCancel,
}: RedactionUploadModalProps) {
  const [category, setCategory] = useState('');
  const {
    uploadWithRedaction,
    uploadProgress,
    isUploading,
    isSuccess,
    isError,
    error,
    reset,
  } = useUploadWithRedaction();

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setCategory('');
      reset();
    }
  }, [open, reset]);

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get redaction summary
  const getRedactionSummary = (): string => {
    const categories = new Map<string, number>();
    redactionMetadata.redactions.forEach((r) => {
      const count = categories.get(r.category) || 0;
      categories.set(r.category, count + 1);
    });

    const parts: string[] = [];
    categories.forEach((count, cat) => {
      parts.push(`${count}x ${cat}`);
    });

    return parts.join(', ');
  };

  // Handle upload
  const handleUpload = async () => {
    try {
      const result = await uploadWithRedaction({
        file: redactedFile,
        filename: originalFilename,
        category: category || undefined,
        redactionMetadata,
      });

      // Notify parent after short delay to show success
      setTimeout(() => {
        onUploadComplete?.(result.documentId);
        onOpenChange(false);
      }, 1000);
    } catch {
      // Error is handled by the hook
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (!isUploading) {
      onCancel?.();
      onOpenChange(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen && !isUploading) {
          handleCancel();
        }
      }}
      title="Geredacteerd document uploaden"
      size="md"
    >
      <div className="space-y-6">
        {/* Document info */}
        <div className="flex items-start gap-4 p-4 bg-mv-gray-50 rounded-lg">
          <Icon name="documentText" className="w-8 h-8 text-mv-blue-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-mv-gray-900 truncate">{originalFilename}</p>
            <p className="text-sm text-mv-gray-500">{formatFileSize(redactedFile.size)}</p>
          </div>
          {isSuccess && (
            <Icon name="checkCircle" className="w-6 h-6 text-mv-green-600 flex-shrink-0" />
          )}
        </div>

        {/* Redaction summary */}
        <div className="flex items-start gap-3 p-4 bg-mv-amber-50 border border-mv-amber-200 rounded-lg">
          <Icon name="eyeSlash" className="w-5 h-5 text-mv-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-mv-amber-800">Redacties toegepast</p>
            <p className="text-sm text-mv-amber-700 mt-1">
              {redactionMetadata.redactions.length} redactie(s): {getRedactionSummary()}
            </p>
            {redactionMetadata.templateId && (
              <p className="text-xs text-mv-amber-600 mt-1">
                Template versie: {redactionMetadata.templateVersion || '1.0'}
              </p>
            )}
          </div>
        </div>

        {/* Privacy info */}
        <div className="flex items-start gap-3 p-3 bg-mv-green-50 border border-mv-green-200 rounded-lg">
          <Icon name="shieldCheck" className="w-5 h-5 text-mv-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-mv-green-800">
              <strong>Alleen de geredacteerde versie wordt opgeslagen.</strong> Het origineel met
              gevoelige informatie wordt niet naar onze servers verzonden.
            </p>
          </div>
        </div>

        {/* Upload progress */}
        {isUploading && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-mv-gray-600">Uploaden...</span>
              <span className="text-mv-gray-900 font-medium">{uploadProgress}%</span>
            </div>
            <ProgressBar value={uploadProgress} />
          </div>
        )}

        {/* Error message */}
        {isError && (
          <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <Icon name="exclamationCircle" className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">
              {error instanceof Error ? error.message : 'Er is een fout opgetreden bij het uploaden'}
            </p>
          </div>
        )}

        {/* Success message */}
        {isSuccess && (
          <div className="flex items-start gap-3 p-3 bg-mv-green-50 border border-mv-green-200 rounded-lg">
            <Icon name="checkCircle" className="w-5 h-5 text-mv-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-mv-green-800">
              Document succesvol geüpload met redactie metadata.
            </p>
          </div>
        )}

        {/* Category selector */}
        {!isSuccess && (
          <FormField label="Categorie (optioneel)" htmlFor="redaction-upload-category">
            <Select
              id="redaction-upload-category"
              options={CATEGORY_OPTIONS}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={isUploading}
            />
          </FormField>
        )}

        {/* Footer actions */}
        <div className="flex justify-between pt-4 border-t border-mv-gray-200">
          <Button variant="secondary" onClick={handleCancel} disabled={isUploading}>
            {isSuccess ? 'Sluiten' : 'Annuleren'}
          </Button>
          {!isSuccess && (
            <Button variant="primary" onClick={handleUpload} disabled={isUploading} isLoading={isUploading}>
              {isUploading ? 'Uploaden...' : 'Upload geredacteerd document'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default RedactionUploadModal;
